(function() {
  const SELECTOR = '.hw-carousel-grid-content';
  const FRACTION_TOLERANCE = 0.001;
  const MIN_CARD_WIDTH_TABLET = 220;
  const MIN_CARD_WIDTH_DESKTOP = 280;
  let rafId = null;

  /**
   * Converts CSS custom property value to a number.
   * @param {HTMLElement} root
   * @param {string} name
   * @returns {number}
   */
  function readVarNumber(root, name) {
    const raw = getComputedStyle(root).getPropertyValue(name).trim();
    return Number.parseFloat(raw);
  }

  /**
   * Returns true when value has a fractional component.
   * @param {number} value
   * @returns {boolean}
   */
  function isFractional(value) {
    return Math.abs(value - Math.round(value)) > FRACTION_TOLERANCE;
  }

  /**
   * Computes current per-card width from container width + configured peek.
   * @param {number} containerWidth
   * @param {number} gap
   * @param {number} slidesPerView
   * @returns {number}
   */
  function getCardWidth(containerWidth, gap, slidesPerView) {
    return (
      (
        containerWidth - (gap * Math.max(0, slidesPerView - 1))
      ) / slidesPerView
    );
  }

  /**
   * Applies effective slide-peek var when fractional peeks are too cramped.
   * @param {HTMLElement} root
   * @param {'tablet'|'desktop'} breakpoint
   * @returns {void}
   */
  function stabilizeBreakpoint(root, breakpoint) {
    const isTablet = breakpoint === 'tablet';
    const layoutClass = isTablet ? 'layout-tablet-carousel' : 'layout-desktop-carousel';
    const mediaQuery = isTablet
      ? '(min-width: 750px) and (max-width: 989px)'
      : '(min-width: 990px)';
    const peekVar = isTablet ? '--slide-peek-tablet' : '--slide-peek-desktop';
    const gapVar = isTablet ? '--gap-tablet' : '--gap-desktop';
    const effectiveVar = isTablet
      ? '--hw-slide-peek-tablet-effective'
      : '--hw-slide-peek-desktop-effective';
    const minCardWidth = isTablet ? MIN_CARD_WIDTH_TABLET : MIN_CARD_WIDTH_DESKTOP;

    root.style.removeProperty(effectiveVar);

    if (!window.matchMedia(mediaQuery).matches || !root.classList.contains(layoutClass)) {
      return;
    }

    const configuredPeek = readVarNumber(root, peekVar);
    const gap = readVarNumber(root, gapVar);
    const containerWidth = root.getBoundingClientRect().width;

    if (
      !Number.isFinite(configuredPeek) ||
      !Number.isFinite(gap) ||
      !Number.isFinite(containerWidth) ||
      configuredPeek <= 1 ||
      !isFractional(configuredPeek)
    ) {
      return;
    }

    const cardWidth = getCardWidth(containerWidth, gap, configuredPeek);
    if (cardWidth >= minCardWidth) {
      return;
    }

    const stabilizedPeek = Math.max(1, Math.floor(configuredPeek));
    if (stabilizedPeek < configuredPeek) {
      root.style.setProperty(effectiveVar, String(stabilizedPeek));
    }
  }

  /**
   * Evaluates all carousel/grid instances.
   * @returns {void}
   */
  function stabilizeAll() {
    document.querySelectorAll(SELECTOR).forEach((root) => {
      stabilizeBreakpoint(root, 'tablet');
      stabilizeBreakpoint(root, 'desktop');
    });
  }

  /**
   * Batches rapid events into a single paint pass.
   * @returns {void}
   */
  function requestStabilize() {
    if (rafId !== null) {
      return;
    }

    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      stabilizeAll();
    });
  }

  /**
   * Tracks `<slideshow-component>` elements that have already been wired for
   * the initial-scroll-position fix below, so re-running the wiring pass
   * (e.g. on `shopify:section:load`) doesn't attach duplicate observers.
   * @type {WeakSet<Element>}
   */
  const initialScrollFixWired = new WeakSet();

  /**
   * Marks a carousel the visitor has taken control of - via an arrow click, a
   * drag, a swipe or the keyboard. Once flagged, no reset-to-first-card logic
   * may move it again.
   *
   * Recorded as a DOM attribute rather than a module-private WeakSet because
   * `hw-featured-collections.js` owns a second reset path and has to honour
   * the same flag; the attribute is the one thing both files can see.
   * @type {string}
   */
  const USER_DRIVEN_ATTR = 'data-hw-user-driven';

  /** @type {{ has: (el: Element) => boolean, add: (el: Element) => void }} */
  const userDriven = {
    has: (el) => el.hasAttribute(USER_DRIVEN_ATTR),
    add: (el) => el.setAttribute(USER_DRIVEN_ATTR, ''),
  };

  /**
   * Where each slideshow was *resting* when its scroll last settled.
   *
   * `index` is the anchor and `left` the exact offset; `scrollWidth` says
   * whether that offset still means anything. Recording only a raw `scrollLeft`
   * is what broke this: a pixel offset captured mid-animation, or measured at a
   * breakpoint the visitor has since left, puts the carousel back between two
   * cards, and `scroll-snap-type: x mandatory` then drags it to whichever card
   * is nearest - a visible jump, with the progress bar and arrows re-syncing to
   * match. Recording only an index would lose the visitor's place in
   * `fluid`-swipe carousels, which have no snap points to land on.
   *
   * @typedef {{ index: number, left: number, scrollWidth: number }} SettledPosition
   * @type {WeakMap<Element, SettledPosition>}
   */
  const settled = new WeakMap();

  /**
   * Quiet period after the last scroll event before a carousel counts as
   * settled. Above scrolling.js's 50ms SCROLL_END_TIMEOUT so the index we
   * record is the one slideshow.js has already synced to, never a frame from
   * part-way through a smooth scroll.
   */
  const SETTLE_DELAY = 150;

  /**
   * Marks a slideshow whose scroll offset theme code is writing right now.
   *
   * Writing the scroll offset bypasses slideshow.js's own Scroller, so its
   * `#userEvent` flag stays true and it reports the resulting move as
   * `slideshow:select` with `userInitiated: true`. Left unguarded, our own
   * correction therefore flags the carousel as visitor-driven, which arms the
   * restore path below for a carousel nobody has touched.
   *
   * A DOM attribute rather than a WeakSet for the same reason as
   * USER_DRIVEN_ATTR: `hw-featured-collections.js` owns a second reset path
   * that writes the same offsets, and has to be able to declare its writes
   * as ours too.
   * @type {string}
   */
  const SELF_DRIVEN_ATTR = 'data-hw-self-driven';

  /** @type {{ has: (el: Element) => boolean, add: (el: Element) => void, remove: (el: Element) => void }} */
  const selfDriven = {
    has: (el) => el.hasAttribute(SELF_DRIVEN_ATTR),
    add: (el) => el.setAttribute(SELF_DRIVEN_ATTR, ''),
    remove: (el) => el.removeAttribute(SELF_DRIVEN_ATTR),
  };

  /**
   * @param {Element} slideshow
   * @returns {Element | null}
   */
  function getScroller(slideshow) {
    return slideshow.querySelector('slideshow-slides');
  }

  /**
   * The offset the scroller comes to rest at for a given card.
   *
   * Mirrors `Scroller#to` (assets/scrolling.js): `offsetLeft` less the
   * scroller's inline start padding, clamped to the scrollable range. Computed
   * rather than remembered so it stays correct after a resize or a breakpoint
   * change.
   *
   * @param {Element} slideshow
   * @param {number} index
   * @returns {number | null} null when the target card cannot be measured.
   */
  function restingOffsetFor(slideshow, index) {
    if (index <= 0) return 0;

    const scroller = getScroller(slideshow);
    if (!(scroller instanceof HTMLElement)) return null;

    const slides = /** @type {*} */ (slideshow).slides;
    const slide = Array.isArray(slides) ? slides[index] : scroller.children[index];
    if (!(slide instanceof HTMLElement)) return null;

    const paddingStart = Number.parseFloat(getComputedStyle(scroller).paddingInlineStart) || 0;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);

    return Math.min(Math.max(slide.offsetLeft - paddingStart, 0), maxScroll);
  }

  /**
   * Moves a scroller to an offset without animating.
   *
   * `slideshow-slides` sets `scroll-behavior: smooth` (base.css), so both a
   * plain `scrollLeft =` assignment and `scrollTo()` animate - which is what
   * made a correction look like someone had clicked an arrow. The inline
   * override is used in preference to `behavior: 'instant'` because
   * ScrollBehavior is a WebIDL enum: Safari versions that predate `instant`
   * reject the whole call with a TypeError rather than ignoring the option.
   *
   * @param {HTMLElement} scroller
   * @param {number} left
   * @returns {void}
   */
  function writeOffset(scroller, left) {
    const previousBehavior = scroller.style.scrollBehavior;

    scroller.style.scrollBehavior = 'auto';
    scroller.scrollLeft = left;

    if (previousBehavior) {
      scroller.style.scrollBehavior = previousBehavior;
    } else {
      scroller.style.removeProperty('scroll-behavior');
    }
  }

  /**
   * Brings slideshow.js's own idea of the current card back in line with the
   * offset we just wrote, so the nav arrows and progress bar agree with what
   * is on screen.
   *
   * This cannot do the move itself. slideshow.js bails out of `select()` when
   * the requested slide is already the current one:
   *
   *   const requestedSlideElement = slides?.[requestedIndex];
   *   if (currentSlide === requestedSlideElement) return;
   *
   * In the broken case `current` still reads the card we want - the browser
   * snapped the DOM elsewhere without slideshow.js having synced yet - so
   * `select()` no-ops precisely when it is needed. Hence: write the offset
   * first, then call this purely to resync.
   *
   * @param {Element} slideshow
   * @param {number} index
   * @returns {void}
   */
  function resyncCurrent(slideshow, index) {
    if (typeof (/** @type {*} */ (slideshow).select) === 'function') {
      /** @type {*} */ (slideshow).select(index, undefined, { animate: false });
    }
  }

  /**
   * Where a slideshow is expected to be resting: the position it last settled
   * at while in view, or its first card if it has not been scrolled yet.
   *
   * @param {Element} slideshow
   * @returns {{ index: number, left: number } | null} null when the target
   *   cannot be measured.
   */
  function expectedPosition(slideshow) {
    const saved = settled.get(slideshow);

    if (!saved) return { index: 0, left: 0 };

    const scroller = getScroller(slideshow);
    if (!(scroller instanceof HTMLElement)) return null;

    /*
     * A changed scrollWidth means the layout moved under us - a resize, or a
     * breakpoint that re-columned the cards - so the recorded pixel offset no
     * longer points at the same card. Re-derive it from the index instead.
     */
    if (scroller.scrollWidth === saved.scrollWidth) {
      return { index: saved.index, left: saved.left };
    }

    const left = restingOffsetFor(slideshow, saved.index);

    return left === null ? null : { index: saved.index, left };
  }

  /**
   * Longest the position is re-checked after a viewport entry, in frames.
   *
   * Safari applies mandatory snapping a frame or more after overflow becomes
   * scrollable, and it can land on the wrong card - so a single corrective
   * write is not enough, the snap has to be out-waited. How long that takes
   * varies, hence a cap plus the early exit below rather than a fixed count.
   */
  const SNAP_SETTLE_MAX_FRAMES = 8;

  /**
   * Consecutive frames the position must already be correct before the hold
   * is released. Two rather than one so a snap that lands *between* checks
   * still gets corrected.
   */
  const SNAP_SETTLE_STABLE_FRAMES = 2;

  /**
   * Holds a carousel at the position it is supposed to be resting at, across
   * the few frames it takes the browser's snapping to settle.
   *
   * Runs on *every* [in-viewport] entry, which is the part that was missing.
   * Enabling scroll on a snap container makes Safari re-snap, frequently onto
   * the last card (see the same note in `hw-featured-collections.js`), and
   * that happens every time the attribute comes back - not only on first
   * load. Correcting just the first entry left every later re-entry
   * mis-snapped, and because [in-viewport] is driven by an IntersectionObserver
   * with a 100px rootMargin, a carousel near the fold re-enters on every small
   * page scroll. That is what made the carousel look like it was reacting to
   * the page scrollbar, with the progress bar and arrows following along.
   *
   * Chrome and Firefox keep the offset across the flip, so the difference
   * check below makes this a no-op there - which is why the symptom is
   * Safari-only.
   *
   * @param {Element} slideshow
   * @returns {void}
   */
  function holdExpectedPosition(slideshow) {
    let framesLeft = SNAP_SETTLE_MAX_FRAMES;
    let stableFrames = 0;

    /*
     * Held for the whole window, not per write: a re-snap that arrives
     * between two of our writes would otherwise read as the visitor moving
     * the carousel.
     */
    selfDriven.add(slideshow);

    const release = () => {
      // Let the scroll events our writes produced dispatch before re-arming.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => selfDriven.remove(slideshow));
      });
    };

    const check = () => {
      framesLeft -= 1;

      const scroller = getScroller(slideshow);
      const expected = expectedPosition(slideshow);

      /*
       * Give up while parked (overflow: hidden ignores the write anyway) or
       * while the visitor is dragging - their gesture outranks us.
       */
      if (
        !slideshow.hasAttribute('in-viewport') ||
        slideshow.hasAttribute('dragging') ||
        !(scroller instanceof HTMLElement) ||
        !expected
      ) {
        release();
        return;
      }

      if (Math.abs(scroller.scrollLeft - expected.left) >= 1) {
        writeOffset(scroller, expected.left);
        resyncCurrent(slideshow, expected.index);
        stableFrames = 0;
      } else {
        stableFrames += 1;
      }

      if (stableFrames >= SNAP_SETTLE_STABLE_FRAMES || framesLeft <= 0) {
        release();
        return;
      }

      requestAnimationFrame(check);
    };

    requestAnimationFrame(check);
  }

  /**
   * Works around a race between slideshow.js and its own [in-viewport] gate:
   *
   * On init, slideshow.js schedules a requestAnimationFrame that calls
   * select(0) -> scrollTo(0, instant) to lock the carousel to the first
   * slide. Separately, a shared IntersectionObserver adds [in-viewport] to
   * the <slideshow-component> once it nears the viewport, which is what
   * flips its <slideshow-slides> from `overflow: hidden` to scrollable
   * (see base.css) - a deliberate guard against iOS Safari compositor-layer
   * crashes from too many simultaneously-scrollable slideshows.
   *
   * scrollTo() is a no-op on overflow: hidden. If the rAF fires before
   * [in-viewport] is set, the scroll-to-zero does nothing, and once overflow
   * becomes scrollable moments later, `scroll-snap-type: x mandatory` can
   * settle on whatever slide the browser was internally sitting on -
   * frequently the last one. The race is timing-dependent (rAF vs.
   * IntersectionObserver callback ordering), which is why it shows up
   * intermittently and more often on slower/mobile devices.
   *
   * Fix: watch each carousel's <slideshow-component> for [in-viewport], and on
   * every entry hold it at the position it is meant to be resting at until the
   * browser's snapping has settled there (`holdExpectedPosition`). If it is
   * already in-viewport by the time this runs, hold immediately instead of
   * waiting on a mutation that already happened.
   *
   * Every entry, not just the first: enabling scroll on a snap container is
   * what triggers the mis-snap, and [in-viewport] is dropped and re-added
   * every time the carousel passes the observer's 100px rootMargin. Safari
   * re-snaps on each of those, so correcting only the first entry left the
   * carousel looking like it was following the page scrollbar.
   *
   * See Dev Notes -> shopify-liquid-limitations.md, "Carousel scroll-to-zero
   * race condition (mobile)".
   *
   * Note: `snippets/slideshow.liquid` renders the passed-in `class` (which is
   * how every carousel gets `.hw-carousel-grid-content`) directly on the
   * `<slideshow-component>` tag itself, not on a wrapping element. So `root`
   * here usually *is* the slideshow-component - `root.querySelector(...)`
   * alone would never match it, since querySelector only looks at
   * descendants. Check `root` itself first, then fall back to a descendant
   * lookup for the few `.hw-carousel-grid-content` cases (e.g. non-carousel
   * layouts, per `hw-loop-carousel-grid.liquid`) where it's a plain wrapper.
   * @param {Element} root
   * @returns {void}
   */
  function wireInitialScrollFix(root) {
    const slideshow = root.matches('slideshow-component') ? root : root.querySelector('slideshow-component');
    if (!slideshow || initialScrollFixWired.has(slideshow)) return;
    initialScrollFixWired.add(slideshow);

    /** @type {number | undefined} */
    let settleTimer;

    /**
     * Whether any scroll event in the current burst came from outside this
     * file. Captured per event rather than read when the burst settles,
     * because the SELF_DRIVEN_ATTR window is only a couple of frames wide.
     */
    let sawForeignScroll = false;

    /**
     * Records where the carousel has come to rest, and whether the visitor is
     * the one who put it there.
     *
     * Deliberately not run straight from the scroll event: mid-scroll the
     * position is whatever is passing by, and a carousel that leaves the
     * viewport during a smooth scroll would be remembered part-way between
     * two cards.
     * @returns {void}
     */
    const recordSettled = () => {
      window.clearTimeout(settleTimer);

      settleTimer = window.setTimeout(() => {
        /*
         * Only record while the carousel is actually scrollable. The clamp
         * that `overflow: hidden` can cause also fires a scroll event, and
         * that one arrives *after* [in-viewport] is gone - so this guard is
         * what stops us from saving the very reset we want to undo.
         */
        if (!slideshow.hasAttribute('in-viewport')) return;

        const scroller = getScroller(slideshow);
        if (!(scroller instanceof HTMLElement)) return;

        const current = /** @type {*} */ (slideshow).current;

        /*
         * A horizontal scroll this file did not cause is the visitor driving
         * the carousel - and the only signal that catches a `fluid`-swipe
         * carousel dragged *within* one card, which never changes `current`
         * and so never emits `slideshow:select`.
         */
        if (sawForeignScroll && scroller.scrollLeft > 0) {
          userDriven.add(slideshow);
        }

        sawForeignScroll = false;

        /*
         * Nothing to remember for a carousel nobody has driven - card one is
         * where it belongs, and `expectedPosition` already defaults there.
         * Skipping the write is what stops a mis-snap that outlasted the hold
         * window from being recorded as the position to restore, which would
         * make the wrong card sticky for the rest of the page's life.
         */
        if (!userDriven.has(slideshow)) return;

        settled.set(slideshow, {
          index: typeof current === 'number' && current > 0 ? current : 0,
          left: scroller.scrollLeft,
          scrollWidth: scroller.scrollWidth,
        });
      }, SETTLE_DELAY);
    };

    /*
     * Once the visitor drives the carousel, its position is theirs, not ours.
     *
     * Both signals used here mean the *carousel* moved. The raw gestures this
     * used to listen for - pointerdown, touchstart, wheel - are how a visitor
     * scrolls the page, and a carousel occupies most of the viewport when you
     * scroll past it, so every one of them flagged carousels nobody had
     * touched. That armed the restore path below on plain page scrolling.
     *
     * `slideshow:select` also covers the nav arrows, which live *outside*
     * <slideshow-component> so their clicks never reach a listener here:
     * slideshow.js sets `userInitiated` from the originating event, so an
     * arrow click arrives as `slideshow:select` with the flag set.
     */
    slideshow.addEventListener('slideshow:select', (event) => {
      if (!(/** @type {*} */ (event).detail?.userInitiated)) return;
      if (selfDriven.has(slideshow)) return;

      userDriven.add(slideshow);
      recordSettled();
    });

    const scroller = getScroller(slideshow);

    /**
     * The most recent offset seen, recorded on every scroll event rather than
     * on settle. Only used to rescue a carousel that leaves the viewport
     * mid-scroll, before `recordSettled` had its chance to run - see `onLeave`.
     * @type {{ index: number, left: number, scrollWidth: number } | null}
     */
    let livePosition = null;

    if (scroller instanceof HTMLElement) {
      scroller.addEventListener(
        'scroll',
        () => {
          if (!selfDriven.has(slideshow)) {
            sawForeignScroll = true;

            const current = /** @type {*} */ (slideshow).current;

            livePosition = {
              index: typeof current === 'number' && current > 0 ? current : 0,
              left: scroller.scrollLeft,
              scrollWidth: scroller.scrollWidth,
            };
          }

          recordSettled();
        },
        { passive: true }
      );
    }

    let inViewport = slideshow.hasAttribute('in-viewport');

    /*
     * Runs on every entry, not just the first. Safari re-snaps - often onto
     * the last card - each time scroll is re-enabled, so the expected position
     * has to be re-asserted on every re-entry rather than once on load.
     */
    const onEnter = () => holdExpectedPosition(slideshow);

    /**
     * Promotes a mid-scroll position to the settled one when the carousel is
     * parked before its settle timer fired, so the visitor's place is not
     * rolled back to wherever they were 150ms earlier.
     *
     * Works off the value the scroll handler already stored: reading
     * `scrollLeft` here instead would flush style, applying `overflow: hidden`
     * and losing the offset before it could be captured.
     * @returns {void}
     */
    const onLeave = () => {
      window.clearTimeout(settleTimer);

      // Same reason as in `recordSettled`: never cement a position on an
      // untouched carousel.
      if (livePosition && userDriven.has(slideshow)) {
        settled.set(slideshow, livePosition);
      }

      livePosition = null;
      sawForeignScroll = false;
    };

    if (inViewport) {
      onEnter();
    }

    /*
     * Stays connected for the life of the page rather than disconnecting after
     * the first correction: [in-viewport] is toggled off again whenever the
     * carousel leaves the viewport, and each re-entry needs its scroll offset
     * put back. Watching the transition (rather than trusting the attribute to
     * be present at callback time) also avoids the case where an add+remove
     * inside one mutation batch left the old observer armed indefinitely, able
     * to fire select(0) long after load - after the visitor had scrolled.
     */
    const observer = new MutationObserver(() => {
      const isInViewport = slideshow.hasAttribute('in-viewport');
      if (isInViewport === inViewport) return;
      inViewport = isInViewport;

      if (isInViewport) onEnter();
      else onLeave();
    });

    observer.observe(slideshow, { attributes: true, attributeFilter: ['in-viewport'] });
  }

  /**
   * Wires the initial-scroll-position fix for every carousel/grid instance.
   * @returns {void}
   */
  function wireAllInitialScrollFixes() {
    document.querySelectorAll(SELECTOR).forEach(wireInitialScrollFix);
  }

  requestStabilize();
  window.addEventListener('resize', requestStabilize);
  document.addEventListener('shopify:section:load', requestStabilize);
  document.addEventListener('shopify:section:reorder', requestStabilize);
  document.addEventListener('shopify:section:select', requestStabilize);
  document.addEventListener('shopify:block:select', requestStabilize);

  wireAllInitialScrollFixes();
  document.addEventListener('shopify:section:load', wireAllInitialScrollFixes);
})();
